import { path } from './client'
import type { ApiClient, Query } from './client'
import type {
  Broadcast,
  BroadcastRecipient,
  BroadcastStatus,
  DeliveryMeasure,
  Instant,
  Locale,
  RecipientState,
} from './types'

/**
 * A message an organization writes and Kura speaks, in the member's language,
 * next time they open the app.
 *
 * Three things about this module are arguments rather than plumbing.
 *
 * `send` is separate from `create`, from `update` and from `schedule`. A
 * broadcast reaches thousands of people in a language most of the staff writing
 * it do not read, and it cannot be recalled once it has been spoken. Saving and
 * sending being the same button is how a draft goes out. Scheduling is its own
 * call for a duller reason that cost this console a real bug: the create and
 * update bodies carry no send time at all, so a composer that put one in them
 * had it dropped in silence by a server that ignores fields it does not know,
 * and "schedule it for Thursday" saved a draft that would never go anywhere
 * while the screen said it was scheduled.
 *
 * `expiresAt` is required, not offered. "The meeting is tomorrow at ten" has to
 * stop being said the day after; a message with no expiry is a message that
 * will eventually be wrong, and the cost of that is measured in people who
 * walked to a meeting that had already happened.
 *
 * `reach` takes a project and an audience rather than a saved broadcast's id,
 * which is what the server serves and is also the better shape: the number can
 * be asked for before anything is written down, and the answer is not attached
 * to a draft that then has to exist for a composer to find out who it would
 * reach. It is a POST despite reading nothing, because an audience filter in a
 * URL is an audience filter in every proxy log between here and the server.
 */

export interface BroadcastListQuery extends Query {
  projectId?: string
  status?: BroadcastStatus
  limit?: number
  cursor?: string
}

export interface BroadcastInput {
  projectId: string
  title: string
  /** What Kura says. Written to be heard, not read. */
  body: string
  locale: Locale
  audience?: BroadcastAudience
  wantsResponse?: boolean
  responsePrompt?: string | null
  /** Required by the server, and required here for the same reason. */
  expiresAt: Instant
}

/**
 * Who inside the project hears it.
 *
 * Narrow on purpose and not a free-form filter: the server stores this on the
 * broadcast and resolves it once, at send, so every field has to be something
 * that still means the same thing in a year. An explicitly empty list is
 * refused rather than interpreted, because `sources: []` reads to a person as
 * "nobody" and to a query builder as "no restriction".
 */
export interface BroadcastAudience {
  sources?: ('code' | 'agent' | 'staff')[]
  codeIds?: string[]
  joinedFrom?: Instant
}

/**
 * How many people an audience contains, and how many of them are still here.
 *
 * `reachable` is the number the composer exists to show and it is deliberately
 * first: how many of these people have opened the app inside
 * `activeWithinDays`, against everybody on the roster. MomConnect registered
 * over a million women and fewer than 6% were still being reached by month
 * twelve, so a recipient total on its own is not evidence that anybody was
 * reached.
 *
 * `measuredAt` is returned because the answer moves. A screen that caches this
 * and shows it beside a send button a week later is showing a number about a
 * different set of people.
 */
export interface ReachEstimate {
  reachable: DeliveryMeasure
  audience: number
  activeWithinDays: number
  measuredAt: Instant
}

export interface RecipientListQuery extends Query {
  state?: RecipientState
  /** Only rows carrying a spoken reply. The point of `wantsResponse`. */
  respondedOnly?: boolean
  limit?: number
  cursor?: string
}

/** What the server reports after a send. A repeat is a success with `added: 0`. */
export interface SendResult {
  broadcast: Broadcast
  recipients: number
  added: number
  alreadySent: boolean
}

export function broadcastsApi(http: ApiClient) {
  return {
    list(query: BroadcastListQuery = {}) {
      return http.list<Broadcast>('/v1/org/broadcasts', query)
    },

    get(id: string) {
      return http.request<Broadcast>(path`/v1/org/broadcasts/${id}`)
    },

    create(input: BroadcastInput) {
      return http.request<Broadcast>('/v1/org/broadcasts', { method: 'POST', body: input })
    },

    update(id: string, input: Partial<BroadcastInput>) {
      return http.request<Broadcast>(path`/v1/org/broadcasts/${id}`, {
        method: 'PATCH',
        body: input,
      })
    },

    /**
     * How many members this audience currently resolves to, and how many of
     * them still open the app.
     *
     * Approximate by nature: membership changes between the estimate and the
     * send, which is what `send`'s `confirmedCount` is for.
     */
    reach(projectId: string, audience?: BroadcastAudience) {
      return http.request<ReachEstimate>('/v1/org/broadcasts/reach', {
        method: 'POST',
        body: { projectId, ...(audience === undefined ? {} : { audience }) },
      })
    },

    /**
     * Sends now.
     *
     * `confirmedCount` is the audience total the composer was shown by `reach`.
     * The server refuses the send if the audience has grown past it by more
     * than a small margin, which is the guard against a filter edited in
     * another tab turning a village into a state between the estimate and the
     * button.
     */
    send(id: string, confirmedCount: number) {
      return http.request<SendResult>(path`/v1/org/broadcasts/${id}/send`, {
        method: 'POST',
        body: { confirmedCount },
      })
    },

    /** Sends it at a chosen time instead. A separate act, and a separate call. */
    schedule(id: string, scheduledFor: Instant) {
      return http.request<Broadcast>(path`/v1/org/broadcasts/${id}/schedule`, {
        method: 'POST',
        body: { scheduledFor },
      })
    },

    /** Stops a scheduled or sending broadcast. Already-played messages are gone. */
    cancel(id: string) {
      return http.request<Broadcast>(path`/v1/org/broadcasts/${id}/cancel`, { method: 'POST' })
    },

    /**
     * Per-recipient delivery.
     *
     * The list exists for following up on individual responses, not for a
     * delivery percentage: the counts on the broadcast itself are the honest
     * summary, and they lead with `played` rather than `sent` for reasons
     * written down on `BroadcastDelivery`.
     *
     * It needs a heavier permission than the counts do, and a caller without it
     * gets a 403. That is the point rather than an inconvenience: the counts
     * name nobody and this names people and quotes them.
     */
    recipients(id: string, query: RecipientListQuery = {}) {
      return http.list<BroadcastRecipient>(path`/v1/org/broadcasts/${id}/recipients`, query)
    },
  }
}

export type BroadcastsApi = ReturnType<typeof broadcastsApi>
